import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Download, Menu, X } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { translations } from '../translations/translations';
import LanguageToggle from './LanguageToggle';
import { navItem } from '../motion/presets';
import '../styles/Navbar.css';

function scrollToSection(e, id) {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
}

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, activeSection } = useAppContext();
  const t = translations[language];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setIsMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navItems = [
    { label: t.nav.about, id: 'about' },
    { label: t.nav.projects, id: 'projects' },
    { label: t.nav.certificates, id: 'certificates' },
    { label: t.nav.blog, id: 'blog', isExternal: true, href: '/blog' },
    { label: t.nav.contact, id: 'contact' },
  ];

  return (
    <motion.header
      className={`navbar ${isScrolled ? 'navbar--scrolled' : ''}`}
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="container navbar__inner">
        <a
          href="/"
          className="navbar__logo"
          aria-label="Home"
          onClick={(e) => scrollToSection(e, 'hero')}
        >
          <img
            src={`${process.env.PUBLIC_URL}/favicon.svg`}
            alt=""
            className="navbar__logo-img"
            width="32"
            height="32"
          />
          <span className="navbar__logo-text">MÖ</span>
        </a>

        <nav className="navbar__nav" aria-label="Main navigation">
          <ul className="navbar__links">
            {navItems.map((item, i) => (
              <motion.li key={item.id} custom={i} variants={navItem} initial="hidden" animate="visible">
                {item.isExternal ? (
                  <Link
                    to={item.href}
                    className={`navbar__link`}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <a
                    href="/"
                    className={`navbar__link ${activeSection === item.id ? 'navbar__link--active' : ''}`}
                    onClick={(e) => scrollToSection(e, item.id)}
                  >
                    {activeSection === item.id && (
                      <motion.span
                        className="navbar__active-bg"
                        layoutId="navActiveBg"
                        transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                      />
                    )}
                    <span className="navbar__link-label">{item.label}</span>
                    {activeSection === item.id && (
                      <motion.span
                        className="navbar__indicator"
                        layoutId="navIndicator"
                        transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                      />
                    )}
                  </a>
                )}
              </motion.li>
            ))}
          </ul>
        </nav>

        <div className="navbar__actions">
          <LanguageToggle />
          <a
            href={`${process.env.PUBLIC_URL}/CV/Mert_Ozkaya_CV.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="navbar__cta"
          >
            <Download size={16} />
            <span>{t.nav.downloadCV}</span>
          </a>
        </div>

        <button
          className="navbar__mobile-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
        >
          <AnimatePresence mode="wait">
            {isMobileMenuOpen ? (
              <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.15 }}>
                <X size={22} />
              </motion.div>
            ) : (
              <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.15 }}>
                <Menu size={22} />
              </motion.div>
            )}
          </AnimatePresence>
        </button>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="navbar__mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          >
            <nav aria-label="Mobile navigation">
              <ul className="navbar__mobile-links">
                {navItems.map((item, i) => (
                  <motion.li
                    key={item.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    {item.isExternal ? (
                      <Link
                        to={item.href}
                        className="navbar__mobile-link"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ) : (
                      <a
                        href="/"
                        className={`navbar__mobile-link ${activeSection === item.id ? 'navbar__mobile-link--active' : ''}`}
                        onClick={(e) => {
                          scrollToSection(e, item.id);
                          setIsMobileMenuOpen(false);
                        }}
                      >
                        {item.label}
                      </a>
                    )}
                  </motion.li>
                ))}
              </ul>
            </nav>
            <div className="navbar__mobile-actions">
              <LanguageToggle />
              <a
                href={`${process.env.PUBLIC_URL}/CV/Mert_Ozkaya_CV.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="navbar__cta navbar__cta--mobile"
              >
                <Download size={16} />
                <span>{t.nav.downloadCV}</span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
